import { type Call, Device } from '@twilio/voice-sdk';
import React from 'react';
import { toast } from 'react-hot-toast';
import { z } from 'zod';

import type { SvrContactPrimaryListObj } from '@poseidon/types/svr/svrContact.type';
import type { SvrWithTwilioFlags } from '@poseidon/types/svr/svrTeam.type';
import type { SvrUserTeamBase } from '@poseidon/types/svr/svrUserTeam.type';

import { api } from '@/utils/api';

import { useTimer } from '~/components/Dialer/useTimer';
import { sleepAsync } from '@/clientOnlyUtils/clientUtils';

type ContextProps = [
  {
    muted: boolean;
    error?: string;
    callOpen: boolean;
    contact?: SvrContactPrimaryListObj;
    recording: boolean;
    formattedTime: string;
    callStarted: boolean;
    callSid?: string;
  },
  {
    startCall: (contact: SvrContactPrimaryListObj) => Promise<void>;
    hangUp: () => void;
    sendDigit: (digit: string) => void;
    setMuted: React.Dispatch<React.SetStateAction<boolean>>;
    destroyDevice: () => void;
    hideDialer: () => void;
  },
];

const defaultValue: ContextProps = [
  {
    recording: false,
    contact: undefined,
    muted: false,
    error: undefined,
    callOpen: false,
    formattedTime: '00:00',
    callStarted: false,
    callSid: undefined,
  },
  {
    startCall: () => Promise.reject(new Error('undefined funtion')),
    hangUp: () => console.log('undefined funtion'),
    sendDigit: () => console.log('undefined funtion'),
    setMuted: () => console.log('undefined funtion'),
    destroyDevice: () => console.log('undefined funtion'),
    hideDialer: () => console.log('undefined funtion'),
  },
];

const CallContext = React.createContext<ContextProps>(defaultValue);

interface CallProviderProps {
  userTeam: SvrUserTeamBase & SvrWithTwilioFlags;
  children: React.ReactNode;
}

export const CallProvider = ({ children, userTeam }: CallProviderProps) => {
  const [dialerConfig, setDialerConfig] = React.useState<{
    call?: Call;
    device?: Device;
    contact?: SvrContactPrimaryListObj;
    contactV2?: SvrContactPrimaryListObj;
  }>({});
  const [muted, setMuted] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>();
  const [callStarted, setCallStarted] = React.useState(false);

  const twilioMutation = api.auth.twilio.useMutation();
  const goalCountMutation = api.user.incrementGoalCount.useMutation();

  const call = dialerConfig.call;
  const device = dialerConfig.device;

  const { startTimer, endTimer, isTimerActive, formattedTime } = useTimer();

  const sendDigit = React.useCallback(
    (digit: string) => {
      call?.sendDigits(digit);
    },
    [call],
  );

  const addListeners = React.useCallback(
    (call?: Call) => {
      call?.on('disconnect', () => {
        endTimer();
        setCallStarted(false);
      });

      call?.on('accept', () => {
        goalCountMutation.mutateAsync({ teamId: userTeam.teamId, goalName: 'call', incrementCount: 1 }).catch((e) => {
          console.log('Failed to update goal count');
          console.log(e);
        });
        startTimer();
      });

      const twilioErrorSchema = z.object({
        description: z.string(),
        explanation: z.string(),
        message: z.string(),
      });

      call?.on('error', (error: unknown) => {
        const parsed = twilioErrorSchema.safeParse(error);
        if (parsed.success) {
          setError(parsed.data.description || parsed.data.explanation || parsed.data.message);
        } else {
          setError('Some unknown error occurred. Please try again later.');
        }
        endTimer();
        setCallStarted(false);
      });
    },
    [endTimer, goalCountMutation, userTeam.teamId, startTimer],
  );

  const startCall = React.useCallback(
    async (contact: SvrContactPrimaryListObj) => {
      setError(undefined);
      try {
        if (userTeam.twilioPhoneNumber == null) {
          toast(`Twilio phone number is missing`, { icon: '📞' });
          return;
        }

        const toPhoneNumber = contact.primaryFields.phone?.data;

        if (toPhoneNumber == null) return;

        let device = dialerConfig.device;
        let call = dialerConfig.call;

        setCallStarted(true);
        const tokenRes = await twilioMutation.mutateAsync({ teamId: userTeam.teamId });
        const token = tokenRes?.accessToken;

        if (token == null) {
          toast('twilio is not set up properly');
          setCallStarted(false);
          return;
        }

        // fetch a token and initiate the device when call is done for the very first time
        // otherwise check if token expiration date has passed and update the token & device if so.
        if (device == null) {
          device = new Device(token, {
            closeProtection: true,
            appName: 'Poseidon',
            // appVersion: chrome.runtime.getManifest().version,
            logLevel: 'trace',
          });
        } else {
          if (device?.token != token) {
            device?.updateToken(token);
            // HACK - Tried this w/o it and it gives an error 31005.
            await sleepAsync(2000);
          }
        }

        if (call) {
          call.disconnect();
          call.removeAllListeners();
          device?.disconnectAll();
        }

        call = await device?.connect({
          params: {
            To: toPhoneNumber,
            From: userTeam.twilioPhoneNumber,
            Record: userTeam.twilioRecordCalls ? 'true' : 'false',
            userId: userTeam.userId,
            teamId: userTeam.teamId,
            contactId: contact.id,
          },
        });
        addListeners(call);
        setDialerConfig({ call, device, contactV2: contact });
      } catch (e) {
        if (e == null) {
          setError('Error happened in calling');
        } else {
          setError((e as Error).message);
        }
      }
    },
    [
      userTeam.twilioPhoneNumber,
      userTeam.teamId,
      userTeam.twilioRecordCalls,
      userTeam.userId,
      dialerConfig.device,
      dialerConfig.call,
      twilioMutation,
      addListeners,
    ],
  );

  const hangUp = React.useCallback(() => {
    call?.disconnect();
    call?.removeAllListeners();
    device?.disconnectAll();
  }, [call, device]);

  const destroyDevice = React.useCallback(() => {
    dialerConfig.device?.disconnectAll();
    dialerConfig.call?.removeAllListeners();
    dialerConfig.call?.disconnect();
    dialerConfig.device?.destroy();
    setDialerConfig({});
  }, [setDialerConfig, dialerConfig]);

  const hideDialer = React.useCallback(() => {
    dialerConfig.device?.disconnectAll();
    dialerConfig.call?.removeAllListeners();
    dialerConfig.call?.disconnect();
    setDialerConfig((config) => ({ ...config, contact: undefined }));
  }, [dialerConfig, setDialerConfig]);

  React.useEffect(() => {
    call?.mute(muted);
  }, [muted, call]);

  const value: ContextProps = [
    {
      recording: isTimerActive && userTeam.twilioRecordCalls,
      muted,
      error,
      callOpen: isTimerActive,
      contact: dialerConfig.contact,
      formattedTime,
      callStarted,
      callSid: call?.parameters?.CallSid,
    },
    {
      startCall,
      hangUp,
      sendDigit,
      setMuted,
      destroyDevice,
      hideDialer,
    },
  ];
  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
};

export const useCallContext = () => React.useContext(CallContext);
